import { ApiErrorState } from "../shared/ApiErrorState";
import { SeasonStatsTable } from "./SeasonStatsTable";
import { StatTierLegend } from "../shared/StatTierLegend";
import { RoutePageMeta } from "../../seo/RoutePageMeta";
import { useSeasonStats } from "./hooks/useSeasonStats";
import type { SeasonStatsRosterFilter } from "../../utils/seasonstats/table";

export const SeasonStatsPage = () => {
  const {
    copy,
    seasons,
    selectedSeason,
    setSelectedSeason,
    visibleRows,
    teamNames,
    teamOptions,
    teamFilter,
    setTeamFilter,
    roleFilter,
    setRoleFilter,
    rosterFilter,
    setRosterFilter,
    sortKey,
    sortDirection,
    handleSort,
    loading,
    error,
    formatSeasonLabel,
    isLiveSeason,
    playerRoles,
  } = useSeasonStats();

  return (
    <div className="mx-auto box-border min-h-screen w-full max-w-[96rem] min-w-0 self-center bg-transparent px-[clamp(0.75rem,2vw,1.25rem)] pt-[12rem] pb-10 font-shentox text-[#e8e8ef] max-md:px-[0.85rem] max-md:pt-[11rem]">
      <RoutePageMeta page="STATS" />
      <div className="flex w-full flex-col gap-5">
        <section className="grid grid-cols-[minmax(0,1fr)_auto] grid-rows-[auto_auto] items-start gap-x-6 gap-y-2 max-md:gap-x-4 max-[520px]:gap-y-3">
          <h1 className="col-start-1 row-start-1 m-0 text-[clamp(2rem,4vw,2.75rem)] font-bold tracking-[-0.02em] max-md:text-[clamp(1.65rem,7vw,2.25rem)]">
            {copy.TITLE}
          </h1>
          <div className="col-start-2 row-span-2 shrink-0 self-start text-right text-[0.9rem] leading-[1.4] text-[#8b8b9a] max-md:text-[0.82rem] max-[520px]:row-span-1 max-[520px]:row-start-1">
            {selectedSeason ? (
              <div className="flex flex-col items-end gap-[0.2rem]">
                <span className="text-[0.72rem] font-semibold tracking-[0.04em] uppercase">
                  {copy.SEASON}
                </span>
                <span className="font-semibold whitespace-nowrap text-[#e8e8ef] max-md:whitespace-normal">
                  {formatSeasonLabel(selectedSeason)}
                </span>
                {isLiveSeason ? (
                  <span className="block max-w-[15rem] text-right text-[0.78rem] leading-[1.35] text-[#5eead4] max-md:max-w-[10rem] max-md:text-[0.72rem] max-[520px]:max-w-[9.5rem]">
                    {copy.CURRENT_SEASON_NOTE}
                  </span>
                ) : null}
              </div>
            ) : (
              <span className="font-semibold whitespace-nowrap text-[#e8e8ef] max-md:whitespace-normal">
                {copy.NO_SEASON_DATA}
              </span>
            )}
          </div>
          <p className="col-start-1 row-start-2 m-0 max-w-[42rem] leading-[1.5] text-[#8b8b9a] max-md:max-w-none max-md:text-[0.92rem] max-md:leading-[1.45] max-[520px]:col-span-full">
            {copy.DESCRIPTION}
          </p>
        </section>

        <section className="surface-card w-full overflow-visible">
          <div className="flex flex-wrap items-center justify-between gap-4 border-b border-b-[var(--surface-card-border)] px-5 py-4 max-md:flex-col max-md:items-start">
            <h2 className="m-0 text-[1.1rem]">{copy.PLAYERS}</h2>
            <div className="flex flex-wrap gap-3">
              <label className="season-stats-filter">
                <span>{copy.FILTER_SEASON}</span>
                <select
                  className="season-stats-filter-select"
                  value={selectedSeason}
                  onChange={(event) => {
                    setSelectedSeason(event.target.value);
                  }}
                  disabled={!seasons.length}
                >
                  {seasons.map((season) => (
                    <option key={season} value={season}>
                      {formatSeasonLabel(season)}
                    </option>
                  ))}
                </select>
              </label>
              <label className="season-stats-filter">
                <span>{copy.FILTER_TEAM}</span>
                <select
                  className="season-stats-filter-select"
                  value={teamFilter}
                  onChange={(event) => {
                    setTeamFilter(event.target.value);
                  }}
                >
                  <option value="all">{copy.ALL_TEAMS}</option>
                  {teamOptions.map((teamId) => (
                    <option key={teamId} value={teamId}>
                      {teamNames[teamId] ?? teamId}
                    </option>
                  ))}
                </select>
              </label>
              <label className="season-stats-filter">
                <span>{copy.FILTER_ROLE}</span>
                <select
                  className="season-stats-filter-select"
                  value={roleFilter}
                  onChange={(event) => {
                    setRoleFilter(event.target.value);
                  }}
                >
                  <option value="all">{copy.ALL_ROLES}</option>
                  {playerRoles.map((role) => (
                    <option key={role} value={role}>
                      {copy.ROLES[role]}
                    </option>
                  ))}
                </select>
              </label>
              <label className="season-stats-filter">
                <span>{copy.FILTER_ROSTER}</span>
                <select
                  className="season-stats-filter-select"
                  value={rosterFilter}
                  onChange={(event) => {
                    const next = event.target.value;
                    if (
                      next === "main" ||
                      next === "substitute" ||
                      next === "benched" ||
                      next === "all"
                    ) {
                      setRosterFilter(next as SeasonStatsRosterFilter);
                    }
                  }}
                >
                  <option value="all">{copy.ALL_PLAYERS}</option>
                  <option value="main">{copy.MAIN_ROSTER_ONLY}</option>
                  <option value="substitute">{copy.SUBSTITUTES_ONLY}</option>
                  <option value="benched">{copy.BENCHED_ONLY}</option>
                </select>
              </label>
            </div>
          </div>

          <div>
            {error ? (
              <ApiErrorState message={error} className="season-stats-empty" />
            ) : loading ? (
              <div className="season-stats-empty">{copy.LOADING}</div>
            ) : (
              <>
                <SeasonStatsTable
                  rows={visibleRows}
                  teamNames={teamNames}
                  sortKey={sortKey}
                  sortDirection={sortDirection}
                  onSort={handleSort}
                />
                <StatTierLegend />
              </>
            )}
          </div>
        </section>
      </div>
    </div>
  );
};
